// Module 10 — pure geometry mapping. Electrical nodes draw at their true Debug 3 coord; reduced edges
// draw along their CONCATENATED polyline (the true OSM path, R2) — never a straight u→v line. These
// feed Cesium's Cartesian3.fromDegrees / fromDegreesArray, so the vertex count flows straight through.
import type { GridNode, GridEdge } from "../domain/index";

export function nodeLonLat(n: GridNode): [number, number] {
  return [n.coord.lon, n.coord.lat];
}

// Render predicate — which nodes get a point primitive. ANONYMOUS `junction`-class nodes are topological
// joints (the surviving degree≥3 pole/tower/splice vertices the reduction keeps for electrical
// connectivity, R13). They carry no inspectable identity and just speckle the map as grey dots that read
// as "poles & towers". They are NOT removed from the model: every edge still draws along its concatenated
// OSM polyline THROUGH these vertices, and the solver keeps every retained junction bus. This is a
// RENDER-ONLY choice (no point primitive, no nodeIndex entry → not picked).
//
// Core-F (realistic-involvement Phase 4): a NAMED junction is a real tap/splice station the user should be
// able to click + inspect, so it IS rendered + indexed. Only truly anonymous (unnamed) junctions stay
// suppressed as pole/tower clutter (their coords still live on the edge polylines + in the solver).
export function isRenderableNode(n: GridNode): boolean {
  if (n.cls !== "junction") return true;
  return !!(n.name && n.name.trim());
}

export function edgeVertexCount(e: GridEdge): number {
  return e.polyline.length;
}

// Per-parallel-circuit perpendicular fan (DEGREES), render-only; the electrical model keeps the one true
// OSM path. Real parallel circuits share ONE transmission corridor / right-of-way, so the bundle must read
// as a SINGLE line (a slightly-wide band) — NOT a spray of separate "extra wires". A small per-circuit step,
// HARD-CAPPED to a narrow total width, keeps a 14-circuit corridor ~one ROW wide instead of fanning ~650 m
// across the map (the over-fan that read as fabricated extra lines + a chaotic arrow-star at junctions).
const SIBLING_SPREAD_DEG = 0.00006; // ~7 m per circuit
const SIBLING_SPREAD_MAX_DEG = 0.00012; // cap: the whole bundle stays ≤ ~13 m wide (one right-of-way)

// The render polyline as [lon,lat] pairs, with the sibling perpendicular fan applied. BOTH the drawn
// PolylineCollection and the marching flow-glyph track use this so arrows ride their own fanned line
// (not the shared centerline).
export function edgeRenderPolyline(e: GridEdge): [number, number][] {
  const poly = e.polyline;
  const k = e.circuitIndex ?? 1;
  // fan a sibling ONLY if it is genuinely COINCIDENT with another (same physical route ⇒ overlapping lines).
  // A different-route parallel (coincident !== true) already draws distinctly — keep it on its true OSM path.
  if (e.corridorId != null && k > 1 && e.coincident === true && poly.length >= 2) {
    // fan circuit k perpendicular to the corridor's overall direction (circuit 1 stays on the true path)
    const lat0 = poly[0]!.lat;
    const cs = Math.cos((lat0 * Math.PI) / 180) || 1; // lon→metric compression at this latitude
    const a = poly[0]!;
    const b = poly[poly.length - 1]!;
    let ax = (b.lon - a.lon) * cs;
    let ay = b.lat - a.lat;
    const n = Math.hypot(ax, ay) || 1;
    ax /= n;
    ay /= n;
    const d = Math.min((k - 1) * SIBLING_SPREAD_DEG, SIBLING_SPREAD_MAX_DEG); // capped perpendicular offset (deg)
    const dLon = (-ay * d) / cs; // perpendicular (−ay, ax); lon de-compressed back to degrees
    const dLat = ax * d;
    return poly.map((c) => [c.lon + dLon, c.lat + dLat]);
  }
  return poly.map((c) => [c.lon, c.lat]);
}

// flattened [lon0,lat0, lon1,lat1, …] for Cartesian3.fromDegreesArray
export function edgeDegreesArray(e: GridEdge): number[] {
  const out: number[] = [];
  for (const [lon, lat] of edgeRenderPolyline(e)) out.push(lon, lat);
  return out;
}
